import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Divider,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton
} from '@mui/material';
import {
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  Cancel as CancelIcon,
  Event as EventIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  AttachMoney as AttachMoneyIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { amenityAPI, tenantSettingsAPI, transactionAPI, memberProfileAPI, amenityScheduleAPI } from '../../services/api';
import { AmenityBooking, UserRole, Transaction, MemberProfile, BookingGuest } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { formatDate, formatTimeString } from '../../utils/dateUtils';

const AmenityBookingDetailPage: React.FC = () => {
  const { amenityId, bookingId } = useParams<{ amenityId: string; bookingId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [booking, setBooking] = useState<AmenityBooking | null>(null);
  const [userRole, setUserRole] = useState<'host' | 'guest' | 'admin' | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [transaction, setTransaction] = useState<any | null>(null);
  const [loadingTransaction, setLoadingTransaction] = useState(false);
  const [memberProfile, setMemberProfile] = useState<MemberProfile | null>(null);
  const [loadingMemberProfile, setLoadingMemberProfile] = useState(false);
  const [guestMemberProfiles, setGuestMemberProfiles] = useState<{ [memberId: string]: MemberProfile }>({});
  const [loadingGuestProfiles, setLoadingGuestProfiles] = useState(false);
  const [amenityName, setAmenityName] = useState<string>('');

  // Cancellation dialog state
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [adminOverride, setAdminOverride] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Edit players dialog state
  const [openEditPlayersDialog, setOpenEditPlayersDialog] = useState(false);
  const [editedGuests, setEditedGuests] = useState<BookingGuest[]>([]);
  const [editingPlayers, setEditingPlayers] = useState(false);
  const [editPlayerError, setEditPlayerError] = useState<string | null>(null);
  const [feePreview, setFeePreview] = useState<any | null>(null);
  const [loadingFeePreview, setLoadingFeePreview] = useState(false);
  const [showEditConfirmation, setShowEditConfirmation] = useState(false);
  const [memberProfiles, setMemberProfiles] = useState<MemberProfile[]>([]);
  const [loadingMemberProfiles, setLoadingMemberProfiles] = useState(false);
  const [playerCountRestrictions, setPlayerCountRestrictions] = useState<{
    enabled: boolean;
    minPlayers: number;
    maxPlayers: number;
  }>({ enabled: false, minPlayers: 1, maxPlayers: 2 });
  const editDialogContentRef = useRef<HTMLDivElement>(null);

  // Cancellation window settings
  const [cancellationWindowHours, setCancellationWindowHours] = useState(24);
  const [hoursUntilBooking, setHoursUntilBooking] = useState<number | null>(null);
  const [canCancelWithoutPenalty, setCanCancelWithoutPenalty] = useState(false);

  // Check if user is admin
  const isAdmin = user && [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.COACH].includes(user.role);
  const canEditBooking = user && [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.EMPLOYEE].includes(user.role);
  const isMember = user && user.role === UserRole.MEMBER;
  
  // Check user role from backend response
  const isActualHost = userRole === 'host';
  const isGuest = userRole === 'guest';
  const isAdminViewing = userRole === 'admin';

  // Fetch booking details and tenant settings
  useEffect(() => {
    if (amenityId && bookingId) {
      fetchBookingDetails();
      fetchTenantSettings();
      fetchAmenityName();
    }
  }, [amenityId, bookingId]);

  // Calculate hours until booking
  useEffect(() => {
    if (booking) {
      calculateHoursUntilBooking();
    }
  }, [booking]);

  const fetchBookingDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await amenityAPI.getAmenityBookingById(amenityId!, bookingId!);
      setBooking(response.data);
      setUserRole(response.data.userRole || null);
      
      // Fetch related transaction if available
      if (response.data?.id) {
        fetchRelatedTransaction(response.data.id);
      }
      
      // Fetch member profile if memberId is available
      if ((response.data as any)?.memberId) {
        fetchMemberProfile((response.data as any).memberId);
      }
      
      // Fetch guest member profiles
      if (response.data?.guests && Array.isArray(response.data.guests)) {
        fetchGuestMemberProfiles(response.data.guests);
      }
    } catch (error: any) {
      console.error('Error fetching booking details:', error);
      const errorMessage = error.response?.data?.message || 'Failed to fetch booking details';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchMemberProfile = async (memberId: string) => {
    try {
      setLoadingMemberProfile(true);
      const response = await memberProfileAPI.getMemberProfileById(memberId);
      setMemberProfile(response.data);
    } catch (error: any) {
      console.error('Error fetching member profile:', error);
      // Don't show error to user as this is supplementary information
    } finally {
      setLoadingMemberProfile(false);
    }
  };

  const fetchGuestMemberProfiles = async (guests: any[]) => {
    try {
      setLoadingGuestProfiles(true);
      const memberGuests = guests.filter(g => g.type === 'member' && g.memberId);
      const profiles: { [memberId: string]: MemberProfile } = {};
      
      await Promise.all(
        memberGuests.map(async (guest) => {
          try {
            const response = await memberProfileAPI.getMemberProfileById(guest.memberId);
            profiles[guest.memberId] = response.data;
          } catch (error) {
            console.error(`Error fetching guest member profile ${guest.memberId}:`, error);
          }
        })
      );
      
      setGuestMemberProfiles(profiles);
    } catch (error: any) {
      console.error('Error fetching guest member profiles:', error);
    } finally {
      setLoadingGuestProfiles(false);
    }
  };

  const fetchAmenityName = async () => {
    try {
      const response = await amenityAPI.getAmenityById(amenityId!);
      setAmenityName(response.data?.name || '');
    } catch (error: any) {
      console.error('Error fetching amenity name:', error);
    }
  };

  const fetchRelatedTransaction = async (bookingId: string) => {
    try {
      setLoadingTransaction(true);
      // Fetch transactions with search for booking ID
      const response = await transactionAPI.getAll({
        type: 'amenity_booking',
        search: bookingId
      });
      
      if (response.data && response.data.length > 0) {
        // Find transaction that matches this booking ID
        const matchingTransaction = response.data.find((t: any) => t.relatedEntityId === bookingId);
        if (matchingTransaction) {
          setTransaction(matchingTransaction);
        }
      }
    } catch (error: any) {
      console.error('Error fetching related transaction:', error);
      // Don't show error to user as transaction might not exist
    } finally {
      setLoadingTransaction(false);
    }
  };

  const fetchTenantSettings = async () => {
    try {
      const response = await tenantSettingsAPI.getAllSettings();
      const settings = response.data;
      
      // Get cancellation window from tenant settings
      const cancelHours = settings.cancelWithoutPenaltyHrs || 24;
      setCancellationWindowHours(cancelHours);
    } catch (error: any) {
      console.error('Error fetching tenant settings:', error);
      // Use default if fetch fails
      setCancellationWindowHours(24);
    }
  };

  const calculateHoursUntilBooking = () => {
    if (!booking) return;

    try {
      // The backend returns bookingDate as YYYY-MM-DD and startTime as HH:MM:SS
      const bookingData = booking as any;
      const dateStr = bookingData.bookingDate;
      const timeStr = bookingData.startTime;
      
      if (!dateStr || !timeStr) {
        setHoursUntilBooking(null);
        setCanCancelWithoutPenalty(false);
        return;
      }
      
      // Parse the date and time
      const [year, month, day] = dateStr.split('-').map(Number);
      const [hours, minutes] = timeStr.split(':').map(Number);
      
      // Create booking date in local timezone
      const bookingDate = new Date(year, month - 1, day, hours, minutes, 0);
      const now = new Date();
      
      // Calculate difference in milliseconds
      const diffMs = bookingDate.getTime() - now.getTime();
      
      // Convert to hours
      const diffHours = diffMs / (1000 * 60 * 60);
      
      setHoursUntilBooking(diffHours);
      setCanCancelWithoutPenalty(diffHours >= cancellationWindowHours);
    } catch (error) {
      console.error('Error calculating hours until booking:', error);
      setHoursUntilBooking(null);
      setCanCancelWithoutPenalty(false);
    }
  };

  const handleOpenCancelDialog = () => {
    setCancelError(null);
    setCancelReason('');
    setAdminOverride(false);
    setOpenCancelDialog(true);
  };

  const handleCancelBooking = async () => {
    if (!booking || !amenityId || !bookingId) return;

    try {
      setCancelling(true);
      setCancelError(null);

      // Prepare request body for admin override and cancellation note
      const requestBody: any = {};
      if (adminOverride) {
        requestBody.overrideCancellationWindow = true;
      }
      if (cancelReason.trim()) {
        requestBody.cancellationNote = cancelReason.trim();
      }

      const response = await amenityAPI.cancelAmenityBooking(amenityId, bookingId, requestBody);
      
      setSuccessMessage(response.data.message || 'Booking cancelled successfully');
      setOpenCancelDialog(false);
      
      // Navigate back after short delay
      setTimeout(() => {
        navigate('/courts-calendar');
      }, 2000);
    } catch (error: any) {
      console.error('Error cancelling booking:', error);
      const errorMessage = error.response?.data?.message || 'Failed to cancel booking';
      setCancelError(errorMessage);
    } finally {
      setCancelling(false);
    }
  };

  // Edit Players Functions
  const handleOpenEditPlayersDialog = async () => {
    if (!booking) return;
    
    setEditPlayerError(null);
    setEditedGuests([...(booking.guests || [])]);
    setOpenEditPlayersDialog(true);
    
    // Fetch member profiles for dropdown
    await fetchMemberProfilesForEdit();
    
    // Fetch player count restrictions
    await fetchPlayerCountRestrictions();
  };

  const fetchMemberProfilesForEdit = async () => {
    try {
      setLoadingMemberProfiles(true);
      const response = await memberProfileAPI.getAllMemberProfiles();
      // Filter to only active members
      const activeMembers = response.data.filter((member: MemberProfile) =>
        member.status === 'active'
      );

      activeMembers.sort((a: MemberProfile, b: MemberProfile) => {
        const firstNameCompare = a.firstName.localeCompare(b.firstName);
        if (firstNameCompare !== 0) return firstNameCompare;
        return a.lastName.localeCompare(b.lastName);
      });
      
      setMemberProfiles(activeMembers);
    } catch (error: any) {
      console.error('Error fetching member profiles:', error);
      setEditPlayerError('Failed to fetch member profiles');
    } finally {
      setLoadingMemberProfiles(false);
    }
  };

  const fetchPlayerCountRestrictions = async () => {
    if (!booking || !amenityId) return;

    try {
      const bookingData = booking as any;
      const dateStr = bookingData.bookingDate;
      const startTime = bookingData.startTime;
      const endTime = bookingData.endTime;

      const response = await amenityScheduleAPI.getActiveScheduleForBooking(
        amenityId,
        dateStr,
        startTime,
        endTime
      );

      const activeSchedule = response.data;

      if (activeSchedule && activeSchedule.courtUseRestrictions) {
        setPlayerCountRestrictions({
          enabled: true,
          minPlayers: activeSchedule.minPlayers || 1,
          maxPlayers: activeSchedule.maxPlayers || 2
        });
      } else {
        setPlayerCountRestrictions({ enabled: false, minPlayers: 1, maxPlayers: 2 });
      }
    } catch (error: any) {
      console.error('Error fetching player count restrictions:', error);
      setPlayerCountRestrictions({ enabled: false, minPlayers: 1, maxPlayers: 2 });
    }
  };

  const addGuestToEdit = () => {
    const newGuest: BookingGuest = {
      type: 'member',
      memberId: null,
      guest: null
    };
    setEditedGuests([...editedGuests, newGuest]);
  };

  const removeGuestFromEdit = (index: number) => {
    const newGuests = editedGuests.filter((_, i) => i !== index);
    setEditedGuests(newGuests);
  };

  const updateEditedGuestType = (index: number, type: 'member' | 'guest') => {
    const newGuests = [...editedGuests];
    newGuests[index] = {
      type,
      memberId: type === 'member' ? null : null,
      guest: type === 'guest' ? { name: '', email: '' } : null
    };
    setEditedGuests(newGuests);
  };

  const updateEditedGuestMember = (index: number, memberId: string | null) => {
    const newGuests = [...editedGuests];
    newGuests[index] = {
      ...newGuests[index],
      memberId
    };
    setEditedGuests(newGuests);
  };

  const updateEditedGuestInfo = (index: number, field: 'name' | 'email', value: string) => {
    const newGuests = [...editedGuests];
    const currentGuest = newGuests[index].guest || { name: '', email: '' };
    newGuests[index] = {
      ...newGuests[index],
      guest: {
        ...currentGuest,
        [field]: value
      }
    };
    setEditedGuests(newGuests);
  };

  const validateEditedGuests = (): string | null => {
    // Check if player count restrictions are enabled
    if (playerCountRestrictions.enabled) {
      const totalPlayers = 1 + editedGuests.length; // 1 for host + guests

      if (totalPlayers < playerCountRestrictions.minPlayers) {
        return `Minimum ${playerCountRestrictions.minPlayers} player(s) required. Please add ${playerCountRestrictions.minPlayers - totalPlayers} more guest(s).`;
      }

      if (totalPlayers > playerCountRestrictions.maxPlayers) {
        return `Maximum ${playerCountRestrictions.maxPlayers} player(s) allowed. Please remove ${totalPlayers - playerCountRestrictions.maxPlayers} guest(s).`;
      }
    }

    // Validate guest data
    for (let i = 0; i < editedGuests.length; i++) {
      const guest = editedGuests[i];

      if (guest.type === 'member' && !guest.memberId) {
        return `Player ${i + 1}: Please select a member`;
      }

      if (guest.type === 'guest') {
        if (!guest.guest?.name || guest.guest.name.trim() === '') {
          return `Player ${i + 1}: Please enter player name`;
        }
        if (!guest.guest?.email || guest.guest.email.trim() === '') {
          return `Player ${i + 1}: Please enter player email`;
        }
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(guest.guest.email)) {
          return `Player ${i + 1}: Please enter a valid email address`;
        }
      }
    }

    return null;
  };

  const handlePreviewEditChanges = async () => {
    if (!booking || !amenityId) return;

    const validationError = validateEditedGuests();
    if (validationError) {
      setEditPlayerError(validationError);
      if (editDialogContentRef.current) {
        editDialogContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }

    try {
      setLoadingFeePreview(true);
      setEditPlayerError(null);

      const bookingData = booking as any;
      const dateStr = bookingData.bookingDate;
      const startTime = bookingData.startTime;
      const endTime = bookingData.endTime;

      // Calculate fee breakdown for the new guest list
      const response = await amenityAPI.calculateAmenityBookingFees(amenityId, {
        bookingDate: dateStr,
        startTime,
        endTime,
        memberProfileId: bookingData.memberId,
        guests: editedGuests.length > 0 ? editedGuests : undefined
      });

      setFeePreview(response.data.feeBreakdown);
      setShowEditConfirmation(true);
    } catch (error: any) {
      console.error('Error fetching fee preview:', error);
      const errorMessage = error.response?.data?.message || 'Failed to calculate fee preview';
      setEditPlayerError(errorMessage);
      if (editDialogContentRef.current) {
        editDialogContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } finally {
      setLoadingFeePreview(false);
    }
  };

  const confirmEditPlayers = async () => {
    if (!booking || !amenityId || !bookingId) return;

    try {
      setEditingPlayers(true);
      setEditPlayerError(null);
      setShowEditConfirmation(false);

      const response = await amenityAPI.updateAmenityBooking(amenityId, bookingId, {
        guests: editedGuests
      });

      setSuccessMessage(response.data.message || 'Booking players updated successfully');
      setOpenEditPlayersDialog(false);

      // Refresh booking details
      await fetchBookingDetails();
    } catch (error: any) {
      console.error('Error updating booking players:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update booking players';
      setEditPlayerError(errorMessage);
      setShowEditConfirmation(false);
      if (editDialogContentRef.current) {
        editDialogContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } finally {
      setEditingPlayers(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !booking) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">
            {error || 'Booking not found'}
          </Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/courts-calendar')}
            sx={{ mt: 2 }}
          >
            Back to Calendar
          </Button>
        </Box>
      </Container>
    );
  }

  // Check if booking can be cancelled
  const bookingHasPassed = hoursUntilBooking !== null && hoursUntilBooking < 0;
  // Only host or admin can cancel, guests cannot
  const canCancel = (isActualHost || isAdminViewing) && booking.status !== 'cancelled' && (!isMember || !bookingHasPassed);
  const showCancellationWarning = canCancel && hoursUntilBooking !== null && hoursUntilBooking < cancellationWindowHours && !bookingHasPassed;

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Breadcrumbs - Hidden for members and on mobile */}
        {!isMember && (
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, display: { xs: 'none', sm: 'flex' } }}>
            <Button
              component={RouterLink}
              to="/dashboard"
              color="inherit"
              startIcon={<HomeIcon />}
              sx={{ textTransform: 'none' }}
            >
              Dashboard
            </Button>
            <Button
              component={RouterLink}
              to="/courts-calendar"
              color="inherit"
              sx={{ textTransform: 'none' }}
            >
              Courts Calendar
            </Button>
            <Typography color="text.primary">Booking Details</Typography>
          </Breadcrumbs>
        )}

        {/* Success Message */}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/courts-calendar')}
          sx={{ mb: 2 }}
        >
          Back to Calendar
        </Button>

        {/* Main Booking Details */}
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'flex-start' }, gap: 2 }}>
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                  Amenity Booking Details
                </Typography>
                <Chip
                  label={getStatusText(booking.status).toLocaleLowerCase() == "on_hold" ? "On Hold" : getStatusText(booking.status)}
                  color={getStatusColor(booking.status)}
                  size="small"
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {canEditBooking && booking.status === 'confirmed' && (
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<EditIcon />}
                    onClick={handleOpenEditPlayersDialog}
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}
                  >
                    Edit Players
                  </Button>
                )}
                {canCancel && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={handleOpenCancelDialog}
                    disabled={Boolean(booking.status === 'cancelled' || (isMember && bookingHasPassed))}
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}
                  >
                    Cancel Booking
                  </Button>
                )}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Cancellation Warning */}
          {isMember && bookingHasPassed && (
            <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight="bold">
                Cancellation Not Allowed
              </Typography>
              <Typography variant="body2">
                The booking cannot be cancelled as the time has already passed.
              </Typography>
            </Alert>
          )}
          {showCancellationWarning && !adminOverride && (
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight="bold">
                Cancellation Window Alert
              </Typography>
              <Typography variant="body2">
                Only {hoursUntilBooking!.toFixed(1)} hours remaining until booking.
                {' '}Cancellation requires at least {cancellationWindowHours} hours notice.
                {isAdmin && ' As an admin, you can override this restriction.'}
              </Typography>
            </Alert>
          )}

          {/* Program Instance Cancelled Warning - Admin Only */}
          {isAdmin && (booking as any).programInstance?.status?.toUpperCase() === 'CANCELLED' && (
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight="bold">
                Associated Program Instance Cancelled
              </Typography>
              <Typography variant="body2">
                This booking is associated with a coaching program instance "{(booking as any).programInstance?.name}" that has been cancelled.
                You may want to cancel this booking as well.
              </Typography>
            </Alert>
          )}

          {/* Coaching Session Cancelled/Missing Warning - Admin Only */}
          {isAdmin && ((booking as any).coachingSession?.status?.toUpperCase() === 'CANCELLED' || 
            ((booking as any).additionalInfo?.coachingSessionId && !(booking as any).coachingSession)) && (
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight="bold">
                {(booking as any).coachingSession?.status?.toUpperCase() === 'CANCELLED' 
                  ? 'Associated Coaching Session Cancelled'
                  : 'Associated Coaching Session Not Found'}
              </Typography>
              <Typography variant="body2">
                {(booking as any).coachingSession?.status?.toUpperCase() === 'CANCELLED'
                  ? `This booking is associated with a coaching session "${(booking as any).coachingSession?.name}" that has been cancelled.`
                  : `This booking references a coaching session "${(booking as any).additionalInfo?.coachingSessionName}" that no longer exists in the system.`}
                You may want to cancel this booking as well.
              </Typography>
            </Alert>
          )}

          {/* Booking Information */}
          <Stack spacing={3}>
            {/* Amenity */}
            {amenityName && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {amenityName}
                </Typography>
              </Box>
            )}
            
            {/* Date and Time */}
            <Box>
              <Typography variant="h6" gutterBottom>
                <EventIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Date & Time
              </Typography>
              <Box sx={{ pl: 4 }}>
                <Typography variant="body1">
                  <strong>Date:</strong> {formatDate((booking as any).bookingDate)}
                </Typography>
                <Typography variant="body1">
                  <strong>Start Time:</strong> {formatTimeString((booking as any).startTime)}
                </Typography>
                <Typography variant="body1">
                  <strong>End Time:</strong> {formatTimeString((booking as any).endTime)}
                </Typography>
                {hoursUntilBooking !== null && hoursUntilBooking > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {hoursUntilBooking.toFixed(1)} hours until booking
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Host Information */}
            <Box>
              <Typography variant="h6" gutterBottom>
                <PersonIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Host Information
              </Typography>
              <Box sx={{ pl: 4 }}>
                <Typography variant="body1">
                  <strong>Name:</strong> {(booking as any).memberName || 'N/A'}
                </Typography>
                <Typography variant="body1">
                  <strong>Email:</strong> {(booking as any).memberEmail || 'N/A'}
                </Typography>
                {memberProfile?.membershipNumber ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Membership #: {memberProfile.membershipNumber}
                  </Typography>
                ) : loadingMemberProfile ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Loading membership info...
                    </Typography>
                  </Box>
                ) : (booking as any).memberId ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Member ID: {(booking as any).memberId}
                  </Typography>
                ) : null}
                {isActualHost && (
                  <Chip label="You are the host" color="primary" size="small" sx={{ mt: 1 }} />
                )}
              </Box>
            </Box>

            {/* Players */}
            {booking.guests && booking.guests.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  <PersonIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Players ({booking.guests.length})
                </Typography>
                <Box sx={{ pl: 4 }}>
                  {loadingGuestProfiles ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        Loading player information...
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={1}>
                      {booking.guests.map((guest, index) => {
                        if (guest.type === 'member' && guest.memberId) {
                          const memberProfile = guestMemberProfiles[guest.memberId];
                          // Check if this guest is the current user
                          const isCurrentUser = isGuest && memberProfile?.id === localStorage.getItem('selectedMemberProfile');
                          
                          return (
                            <Box key={index}>
                              <Typography variant="body1">
                                <strong>Member:</strong>{' '}
                                {memberProfile 
                                  ? `${memberProfile.firstName} ${memberProfile.lastName} (${memberProfile.email})`
                                  : `Member ID: ${guest.memberId}`
                                }
                              </Typography>
                              {isCurrentUser && (
                                <Chip label="You" color="secondary" size="small" sx={{ ml: 1 }} />
                              )}
                            </Box>
                          );
                        } else if (guest.type === 'guest' && guest.guest) {
                          return (
                            <Box key={index}>
                              <Typography variant="body1">
                                <strong>External Guest:</strong> {guest.guest.name} ({guest.guest.email})
                              </Typography>
                            </Box>
                          );
                        }
                        return null;
                      })}
                    </Stack>
                  )}
                </Box>
              </Box>
            )}

            {/* Transaction Details */}
            {loadingTransaction ? (
              <Box>
                <Typography variant="h6" gutterBottom>
                  <AttachMoneyIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Transaction Details
                </Typography>
                <Box sx={{ pl: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Loading transaction details...
                  </Typography>
                </Box>
              </Box>
            ) : transaction ? (
              <Box>
                <Typography variant="h6" gutterBottom>
                  <AttachMoneyIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Transaction Details
                </Typography>
                <Box sx={{ pl: 4 }}>
                  <Typography variant="body1">
                    <strong>Transaction ID:</strong> {transaction.id}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Amount:</strong> ${transaction.amount?.toFixed(2) || '0.00'}
                  </Typography>
                  {transaction.tax > 0 && (
                    <Typography variant="body1">
                      <strong>Tax:</strong> ${transaction.tax.toFixed(2)}
                    </Typography>
                  )}
                  {transaction.discountAmount > 0 && (
                    <Typography variant="body1">
                      <strong>Discount:</strong> -${transaction.discountAmount.toFixed(2)}
                      {transaction.discountPercentage > 0 && ` (${transaction.discountPercentage}%)`}
                    </Typography>
                  )}
                  <Typography variant="body1">
                    <strong>Total Amount:</strong> ${transaction.totalAmount?.toFixed(2) || '0.00'}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Status:</strong>{' '}
                    <Chip
                      label={transaction.status.toUpperCase()}
                      color={
                        transaction.status === 'completed' ? 'success' :
                        transaction.status === 'pending' ? 'warning' :
                        transaction.status === 'cancelled' ? 'default' :
                        'error'
                      }
                      size="small"
                    />
                  </Typography>
                  {transaction.description && (
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      <strong>Description:</strong> {transaction.description}
                    </Typography>
                  )}
                  {transaction.paymentMethod && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Payment Method: {transaction.paymentMethod}
                    </Typography>
                  )}
                  {transaction.transactionDate && (
                    <Typography variant="body2" color="text.secondary">
                      Transaction Date: {new Date(transaction.transactionDate).toLocaleString()}
                    </Typography>
                  )}
                  {transaction.metadata?.adminCancellation && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>Admin Cancellation</strong>
                      </Typography>
                      {transaction.metadata.adminCancellationNote && (
                        <Typography variant="body2">
                          {transaction.metadata.adminCancellationNote}
                        </Typography>
                      )}
                      {transaction.metadata.cancelledBy && (
                        <Typography variant="body2" color="text.secondary">
                          Cancelled by: {transaction.metadata.cancelledBy}
                        </Typography>
                      )}
                    </Alert>
                  )}
                </Box>
              </Box>
            ) : (
              <Box>
                <Typography variant="h6" gutterBottom>
                  <AttachMoneyIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Transaction Details
                </Typography>
                <Box sx={{ pl: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No transaction found for this booking
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Notes */}
            {booking.notes && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Notes
                </Typography>
                <Box sx={{ pl: 4 }}>
                  <Typography variant="body1">{booking.notes}</Typography>
                </Box>
              </Box>
            )}

            {/* Timestamps */}
            <Box>
              <Typography variant="body2" color="text.secondary">
                <strong>Created:</strong> {new Date(booking.createdAt).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Last Updated:</strong> {new Date(booking.updatedAt).toLocaleString()}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Cancel Booking Dialog */}
        <Dialog
          open={openCancelDialog}
          onClose={() => !cancelling && setOpenCancelDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CancelIcon color="error" />
              Cancel Booking
            </Box>
          </DialogTitle>
          <DialogContent>
            {cancelError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {cancelError}
              </Alert>
            )}

            <Typography variant="body1" gutterBottom>
              Are you sure you want to cancel this booking?
            </Typography>

            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Date:</strong> {formatDate((booking as any).bookingDate)}
              </Typography>
              <Typography variant="body2">
                <strong>Time:</strong> {formatTimeString((booking as any).startTime)} - {formatTimeString((booking as any).endTime)}
              </Typography>
              {hoursUntilBooking !== null && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Hours Until Booking:</strong> {hoursUntilBooking.toFixed(1)}
                </Typography>
              )}
            </Box>

            {/* Cancellation Window Warning */}
            {showCancellationWarning && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  This booking is within {cancellationWindowHours} hours. 
                  {!isAdmin && ' Cancellation may result in penalties.'}
                </Typography>
              </Alert>
            )}

            {/* Admin Override Checkbox */}
            {isAdmin && showCancellationWarning && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={adminOverride}
                    onChange={(e) => setAdminOverride(e.target.checked)}
                    color="warning"
                  />
                }
                label={
                  <Typography variant="body2">
                    <strong>Admin Override:</strong> Cancel despite being outside cancellation window
                  </Typography>
                }
                sx={{ mt: 2 }}
              />
            )}

            {/* Optional Cancellation Reason */}
            <TextField
              fullWidth
              label="Cancellation Reason (Optional)"
              multiline
              rows={3}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              sx={{ mt: 2 }}
              placeholder="Provide a reason for cancellation..."
            />

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              All pending transactions will be marked as CANCELLED.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCancelDialog(false)} disabled={cancelling}>
              Keep Booking
            </Button>
            <Button
              onClick={handleCancelBooking}
              color="error"
              variant="contained"
              disabled={cancelling || (!canCancelWithoutPenalty && !adminOverride)}
              startIcon={cancelling ? <CircularProgress size={20} /> : <CancelIcon />}
            >
              {cancelling ? 'Cancelling...' : 'Confirm Cancellation'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Players Dialog */}
        <Dialog
          open={openEditPlayersDialog}
          onClose={() => !editingPlayers && setOpenEditPlayersDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EditIcon />
              Edit Players
            </Box>
          </DialogTitle>
          <DialogContent ref={editDialogContentRef}>
            {editPlayerError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {editPlayerError}
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You can add or remove players from this booking. The booking fees will be automatically recalculated.
            </Typography>

            {/* Host Information (Read-only) */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Host (Cannot be changed)
              </Typography>
              <Typography variant="body2">
                {(booking as any)?.memberName || 'N/A'}
              </Typography>
            </Box>

            {/* Players List */}
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Players ({editedGuests.length})
            </Typography>

            {loadingMemberProfiles ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading member profiles...</Typography>
              </Box>
            ) : (
              <Stack spacing={2} sx={{ mb: 2 }}>
                {editedGuests.map((guest, index) => (
                  <Paper key={index} sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                          <InputLabel>Player Type</InputLabel>
                          <Select
                            value={guest.type}
                            label="Player Type"
                            onChange={(e) => updateEditedGuestType(index, e.target.value as 'member' | 'guest')}
                          >
                            <MenuItem value="member">Member</MenuItem>
                            <MenuItem value="guest">External Guest</MenuItem>
                          </Select>
                        </FormControl>

                        {guest.type === 'member' ? (
                          <FormControl fullWidth size="small">
                            <InputLabel>Select Member</InputLabel>
                            <Select
                              value={guest.memberId || ''}
                              label="Select Member"
                              onChange={(e) => updateEditedGuestMember(index, e.target.value || null)}
                            >
                              {memberProfiles.map((profile) => (
                                <MenuItem key={profile.id} value={profile.id}>
                                  {profile.firstName} {profile.lastName} ({profile.email})
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Stack spacing={1}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Guest Name"
                              value={guest.guest?.name || ''}
                              onChange={(e) => updateEditedGuestInfo(index, 'name', e.target.value)}
                            />
                            <TextField
                              fullWidth
                              size="small"
                              label="Guest Email"
                              type="email"
                              value={guest.guest?.email || ''}
                              onChange={(e) => updateEditedGuestInfo(index, 'email', e.target.value)}
                            />
                          </Stack>
                        )}
                      </Box>
                      <IconButton
                        color="error"
                        onClick={() => removeGuestFromEdit(index)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}

                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addGuestToEdit}
                  fullWidth
                >
                  Add Player
                </Button>
              </Stack>
            )}

            {/* Player Count Info */}
            {playerCountRestrictions.enabled && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Player Requirements:</strong> Minimum {playerCountRestrictions.minPlayers}, Maximum {playerCountRestrictions.maxPlayers} players (including host)
                </Typography>
                <Typography variant="body2">
                  Current: {1 + editedGuests.length} player(s)
                </Typography>
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenEditPlayersDialog(false)} disabled={editingPlayers || loadingFeePreview}>
              Cancel
            </Button>
            <Button
              onClick={handlePreviewEditChanges}
              variant="contained"
              disabled={editingPlayers || loadingFeePreview || loadingMemberProfiles}
              startIcon={loadingFeePreview ? <CircularProgress size={20} /> : null}
            >
              {loadingFeePreview ? 'Calculating...' : 'Preview Changes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Confirmation Dialog */}
        <Dialog
          open={showEditConfirmation}
          onClose={() => !editingPlayers && setShowEditConfirmation(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon color="primary" />
              Confirm Player Changes
            </Box>
          </DialogTitle>
          <DialogContent>
            {editPlayerError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {editPlayerError}
              </Alert>
            )}

            <Typography variant="body1" gutterBottom>
              Review the changes before confirming:
            </Typography>

            {/* Current vs New Players */}
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Current Players: {1 + (booking?.guests?.length || 0)}
              </Typography>
              <Typography variant="subtitle2" fontWeight="bold">
                New Players: {1 + editedGuests.length}
              </Typography>
            </Box>

            {/* Fee Breakdown */}
            {feePreview && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  New Fee Breakdown:
                </Typography>
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Stack spacing={1}>
                    {feePreview.hostFee && (
                      <Box>
                        <Typography variant="body2">
                          <strong>Host Fee:</strong> ${feePreview.hostFee.totalAmount?.toFixed(2) || '0.00'}
                        </Typography>
                        {feePreview.hostFee.splitAmount > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            Split: ${feePreview.hostFee.splitAmount.toFixed(2)}, 
                            Guest Fees: ${feePreview.hostFee.guestFees.toFixed(2)}
                          </Typography>
                        )}
                      </Box>
                    )}
                    {feePreview.memberGuestFees && feePreview.memberGuestFees.length > 0 && (
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          Member Guest Fees:
                        </Typography>
                        {feePreview.memberGuestFees.map((guest: any, idx: number) => (
                          <Typography key={idx} variant="caption" color="text.secondary" display="block">
                            {guest.memberName}: ${guest.totalAmount?.toFixed(2) || '0.00'}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    <Divider />
                    <Typography variant="body1" fontWeight="bold">
                      Grand Total: ${feePreview.grandTotal?.toFixed(2) || '0.00'}
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            )}

            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Transaction Changes:</strong>
              </Typography>
              <Typography variant="body2">
                • Existing transactions for removed players will be CANCELLED
              </Typography>
              <Typography variant="body2">
                • New transactions will be created for added players
              </Typography>
              <Typography variant="body2">
                • All transactions will be recalculated based on the new player count
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowEditConfirmation(false)} disabled={editingPlayers}>
              Go Back
            </Button>
            <Button
              onClick={confirmEditPlayers}
              variant="contained"
              color="primary"
              disabled={editingPlayers}
              startIcon={editingPlayers ? <CircularProgress size={20} /> : <EditIcon />}
            >
              {editingPlayers ? 'Updating...' : 'Confirm Changes'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default AmenityBookingDetailPage;
